import type { ReactNode } from "react";
import { Link, Navigate, type RouteObject } from "react-router";
import { useAuth } from "../../lib/auth";
import { useAppPreferences } from "../../lib/preferences";
import { AccountRecordPage, AccountsPage } from "./pages/AccountsPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { ApprovalsPage } from "./pages/ApprovalsPage";
import { AuditPage } from "./pages/AuditPage";
import { HomePage } from "./pages/HomePage";
import { LiveActivityPage } from "./pages/LiveActivityPage";
import {
  PromotionCreatePage,
  PromotionDetailPage,
  PromotionRevisePage,
  PromotionsListPage,
} from "./pages/PromotionsPages";
import { ProviderCreatePage, ProvidersPage } from "./pages/ProvidersPage";
import { ProviderRecordPage } from "./pages/ProviderRecordPage";
import { DeletionRequestsPage, ModerationPage, TeamPage } from "./pages/QueuePages";
import { SearchPage } from "./pages/SearchPage";
import { ProviderSettlementPage, SettlementsPage } from "./pages/SettlementsPage";
import { VenueRecordPage } from "./pages/VenueRecordPage";
import { VenuesPage } from "./pages/VenuesPage";
import { PageHeader } from "./ui";

/** Support can open the URL, but the page explains it's admin-only (the API enforces it too). */
function AdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { t } = useAppPreferences();
  if (user?.role === "admin") return <>{children}</>;
  return (
    <div className="console-page">
      <PageHeader title={t("Admins only")} description={t("This page is limited to admin accounts. Ask an admin if you need something here.")} />
      <Link className="console-link" to="/admin">{t("Back to Home")}</Link>
    </div>
  );
}

function NotFound() {
  const { t } = useAppPreferences();
  return (
    <div className="console-page">
      <PageHeader title={t("Page not found")} description={t("This console page doesn't exist.")} />
      <Link className="console-link" to="/admin">{t("Back to Home")}</Link>
    </div>
  );
}

export const adminConsoleRoutes: RouteObject[] = [
  { index: true, element: <HomePage /> },
  { path: "search", element: <SearchPage /> },
  { path: "activity", element: <LiveActivityPage /> },
  { path: "approvals", element: <ApprovalsPage /> },
  { path: "moderation", element: <ModerationPage /> },
  { path: "deletion-requests", element: <DeletionRequestsPage /> },
  { path: "providers", element: <ProvidersPage /> },
  { path: "providers/new", element: <AdminOnly><ProviderCreatePage /></AdminOnly> },
  { path: "providers/:providerId", element: <ProviderRecordPage /> },
  { path: "venues", element: <VenuesPage /> },
  { path: "venues/:venueId", element: <VenueRecordPage /> },
  { path: "accounts", element: <AccountsPage /> },
  { path: "accounts/:accountId", element: <AccountRecordPage /> },
  { path: "settlements", element: <AdminOnly><SettlementsPage /></AdminOnly> },
  { path: "settlements/:providerId", element: <AdminOnly><ProviderSettlementPage /></AdminOnly> },
  // Booking campaigns: reads are open to Admin and Support; only the
  // write buttons and forms check for Admin (never an AdminOnly page block).
  { path: "promotions", element: <PromotionsListPage /> },
  { path: "promotions/new", element: <PromotionCreatePage /> },
  { path: "promotions/:promotionId", element: <PromotionDetailPage /> },
  { path: "promotions/:promotionId/revise", element: <PromotionRevisePage /> },
  { path: "analytics", element: <AnalyticsPage /> },
  { path: "audit", element: <AuditPage /> },
  { path: "team", element: <AdminOnly><TeamPage /></AdminOnly> },
  // Old single-page tab names people may have bookmarked.
  { path: "queue", element: <Navigate replace to="/admin/approvals" /> },
  { path: "*", element: <NotFound /> },
];
